import React, { FC, Fragment } from 'react';

import { FeedKey, GroupFragment } from 'api';
import { Desktop, Layouts, Loaders, Mobile } from 'components';
import { useProtectedClient, useCurrentUser } from 'hooks';
import { isAdminUser } from 'helpers';
import { GROUP_MEMBERS_LIMIT } from 'app-constants';
import { QueryVariablesProvider } from 'providers';

import { useGetGroupFollowers } from 'features/groups/hooks';
import { isGroupOwnerOrModerator } from 'features/groups/helpers';

import DesktopBanner from './DesktopBanner';
import FeedMenu from './FeedMenu';
import MobileBanner from './MobileBanner';
import MemberTable from './Members/Table';
import AddMemberInput from './Members/AddMemberInput';
import MemberTableLoader from './Members/MemberTableLoader';

interface IProps {
  group: GroupFragment;
  isFollowing: boolean;
}

const GroupMembers: FC<IProps> = ({ group, isFollowing }) => {
  const currentUser = useCurrentUser();
  const client = useProtectedClient();
  const allowSort = !client.sort_mode;

  const variables = { data: { id: group.id, limit: GROUP_MEMBERS_LIMIT, feed: FeedKey.Group } };
  const { results, isInitialLoad, sentinelRef, isExhausted } = useGetGroupFollowers(variables);

  function renderMemberInput() {
    if (!isGroupOwnerOrModerator(currentUser, group.id) && !isAdminUser(currentUser)) {
      return null;
    }

    if (group.is_private) {
      return <AddMemberInput group={group} members={results} />;
    }

    return null;
  }

  function renderMenu() {
    return <FeedMenu selectedGroup={group} allowSort={allowSort} />;
  }

  function renderMain() {
    if (isInitialLoad) {
      return <MemberTableLoader rows={5} />;
    }

    return (
      <Fragment>
        {renderMemberInput()}
        <MemberTable feed_follows={results} group={group} />
        {!isExhausted && (
          <div ref={sentinelRef}>
            <Loaders.Standard />
          </div>
        )}
      </Fragment>
    );
  }

  function renderBanner() {
    const bannerProps = {
      group,
      menu: renderMenu(),
    };

    return (
      <Fragment>
        <Desktop>
          <DesktopBanner {...bannerProps} />
        </Desktop>
        <Mobile>
          <MobileBanner isFollowing={isFollowing} {...bannerProps} />
        </Mobile>
      </Fragment>
    );
  }

  return (
    <QueryVariablesProvider value={variables}>
      <Layouts.AppMain1Column header={renderBanner()} main={renderMain()} />
    </QueryVariablesProvider>
  );
};

export default GroupMembers;
